import React from "react";
import { Project } from "@/types";
import DashboardMap from "@/components/DashboardMap";
import { useLanguage } from "@/contexts/LanguageContext";
import ProjectCardList from "./ProjectCardList";

interface MapContainerProps {
  mapCenter: [number, number];
  projects: Project[];
  selectedProject: Project | null;
  projectToBatchIds: Record<string, string[]>;
  batchTitles: Record<string, string>;
  onSelectProject: (project: Project) => void;
  onCloseProjectPanel: () => void;
  customerColor?: string;
  batchPlannedInvestments?: Record<string, any>;
  batchMeasures?: any[];
  isLoadingBatchData?: boolean;
}

const MapContainer: React.FC<MapContainerProps> = ({
  mapCenter,
  projects,
  selectedProject,
  projectToBatchIds,
  batchTitles,
  onSelectProject,
  customerColor = "#00383c",
  isLoadingBatchData = false,
}) => {
  const { t } = useLanguage();

  // Add custom pulse animation based on customer color
  React.useEffect(() => {
    if (customerColor) {
      const style = document.createElement("style");
      style.textContent = `
        @keyframes dot-pulse {
          0% {
            box-shadow: 0 0 0 0 ${customerColor
              .replace("rgb(", "rgba(")
              .replace(")", ", 0.7)")};
          }
          70% {
            box-shadow: 0 0 0 6px ${customerColor
              .replace("rgb(", "rgba(")
              .replace(")", ", 0)")};
          }
          100% {
            box-shadow: 0 0 0 0 ${customerColor
              .replace("rgb(", "rgba(")
              .replace(")", ", 0)")};
          }
        }

        .dot-pulse {
          animation: dot-pulse 1.5s infinite;
        }
      `;
      document.head.appendChild(style);

      return () => {
        document.head.removeChild(style);
      };
    }
  }, [customerColor]);

  return (
    <div className="mb-10 mx-auto project-section">
      <h2 className="text-2xl font-semibold mb-6">
        {t("dashboard.supportedProjects.title")}
      </h2>
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Project cards - 2/3 width on large screens */}
        <div className="w-full lg:w-2/3 order-2 lg:order-1">
          <ProjectCardList
            projects={projects || []}
            selectedProject={selectedProject}
            onSelectProject={onSelectProject}
            batchTitles={batchTitles}
            projectToBatchIds={projectToBatchIds}
            customerColor={customerColor}
            isLoadingBatchData={isLoadingBatchData}
          />
        </div>

        {/* Map section - 1/3 width on large screens with sticky positioning */}
        <div className="w-full lg:w-1/3 order-1 lg:order-2 lg:sticky lg:top-[100px] h-[400px] lg:h-[calc(100vh-120px)]">
          <div className="rounded-xl overflow-hidden border border-gray-200 shadow-sm relative h-full">
            <DashboardMap
              coordinates={mapCenter}
              projects={projects || []}
              onSelectProject={onSelectProject}
              selectedProject={selectedProject}
              customerColor={customerColor}
              t={t}
            />

            <div className="absolute bottom-4 right-4 bg-white rounded-full py-2 px-4 shadow-md flex items-center gap-2">
              <div
                className="h-3 w-3 rounded-full dot-pulse"
                style={{ backgroundColor: customerColor }}
              ></div>
              <span className="text-sm font-medium">
                {t("dashboard.supportedProjects.exploreProjects")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapContainer;
